import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetAdCategoryQuery, useCreateAdCategoryMutation, useUpdateAdCategoryMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const AdminAdCategoryForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const parent_id = searchParams.get('parent_id');
  const isEdit = !!id;
  
  const { data, isLoading } = useGetAdCategoryQuery(id!, { skip: !isEdit });
  const [createAdCategory, { isLoading: isCreating }] = useCreateAdCategoryMutation();
  const [updateAdCategory, { isLoading: isUpdating }] = useUpdateAdCategoryMutation();

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    status: true,
    sort_order: 0,
    icon: '',
  });
  const [iconFile, setIconFile] = useState<File | null>(null);

  useEffect(() => {
    if (isEdit && data?.data) {
      const category = data.data;
      setFormData({
        title: category.title || '',
        title_ar: category.title_ar || '',
        description: category.description || '',
        description_ar: category.description_ar || '',
        status: category.status !== undefined ? category.status : true,
        sort_order: category.sort_order !== undefined ? category.sort_order : 0,
        icon: category.icon || '',
      });
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleIconChange = (file: File | null) => {
    setIconFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedTitle = formData.title.trim();
    const trimmedTitleAr = formData.title_ar.trim();
    
    if (!trimmedTitle || trimmedTitle.length < 3) {
      showError(t('Title is required and must be at least 3 characters long.'));
      return;
    }

    if (!trimmedTitleAr || trimmedTitleAr.length < 3) {
      showError(t('Title Arabic is required and must be at least 3 characters long.'));
      return;
    }
    
    try {
      const submitData = new FormData();
      submitData.append('title', trimmedTitle);
      submitData.append('title_ar', trimmedTitleAr);
      submitData.append('description', formData.description);
      submitData.append('description_ar', formData.description_ar);
      submitData.append('sort_order', formData.sort_order.toString());
      submitData.append('status', formData.status ? '1' : '0');
      
      if (iconFile) {
        submitData.append('icon', iconFile);
      }

      if (parent_id) {
        submitData.append('parent_id', parent_id);
      }

      if (isEdit) {
        // For PUT/PATCH requests with FormData, we often need _method=PUT in Laravel
        submitData.append('_method', 'PUT');
        const result = await updateAdCategory({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || t('Ad Category updated successfully!'));
      } else {
        const result = await createAdCategory(submitData).unwrap();
        showSuccess(result.message || t('Ad Category created successfully!'));
      }

      navigate(parent_id ? `/admin/ad-categories?parent_id=${parent_id}` : '/admin/ad-categories');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || t('Validation failed'));
      } else {
        showError(err?.data?.message || (isEdit ? t('Failed to update ad category. Please try again.') : t('Failed to create ad category. Please try again.')));
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? t('Edit Ad Category') : (parent_id ? t('Add New Subcategory') : t('Add New Ad Category'))}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">{t('Dashboard')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-categories'); }} className="text-muted text-hover-primary">{t('Ad Categories')}</a>
                </li>
                {parent_id && (
                  <>
                    <li className="breadcrumb-item">
                      <span className="bullet bg-gray-400 w-5px h-2px"></span>
                    </li>
                    <li className="breadcrumb-item text-muted">
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/admin/ad-categories?parent_id=${parent_id}`); }} className="text-muted text-hover-primary">{t('Subcategories')}</a>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Category Title')}
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">{t('Title Arabic')}</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder={t('Enter Category Title in Arabic')}
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Description')}</label>
                      <textarea
                        className="form-control form-control-solid"
                        placeholder={t('Enter Category Description')}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange as any}
                        rows={3}
                      />
                    </div>

                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Description Arabic')}</label>
                      <textarea
                        className="form-control form-control-solid"
                        placeholder={t('Enter Category Description in Arabic')}
                        name="description_ar"
                        value={formData.description_ar}
                        onChange={handleInputChange as any}
                        rows={3}
                      />
                    </div>

                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Icon')}</label>
                      <ImageUpload
                        existingImage={formData.icon}
                        onImageChange={handleIconChange}
                        label={t('Category Icon')}
                      />
                    </div>
                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Sort Order')}</label>
                        <input
                          className="form-control form-control-solid"
                          type="number"
                          name="sort_order"
                          value={formData.sort_order}
                          onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-4 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">{t('Status')}</label>
                      <div className="form-check form-switch form-check-custom form-check-solid">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="status"
                          checked={formData.status}
                          onChange={handleInputChange}
                          id="statusSwitch"
                        />
                        <label className="form-check-label" htmlFor="statusSwitch">
                          {formData.status ? t('Active') : t('Inactive')}
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate(parent_id ? `/admin/ad-categories?parent_id=${parent_id}` : '/admin/ad-categories')}>
                      {t('Discard')}
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">{t('Submit')}</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          {t('Please wait...')}
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdCategoryForm;